import { useState, useCallback } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import {
  useTaskCompletion,
  useCompleteTask,
  useDeclineTask,
  useAddComment,
} from "../hooks/taskCompletionHook"
import { WorkflowTaskCommentDto } from "../dto/taskCompletionDto"
import {
  CheckCircle,
  XCircle,
  Calendar,
  User,
  Building,
  Clock,
  AlertCircle,
  Loader2,
  MessageSquare,
  Send,
} from "lucide-react"
import { Button } from "@/modules/shared/ui/components/button"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Badge } from "@/modules/shared/ui/components/badge"
import { toast } from "sonner"
import { useRecaptcha } from "@/modules/shared/hooks/use-recaptcha"

function CommentBubble({ comment }: { comment: WorkflowTaskCommentDto }) {
  const isCompleted = comment.commentType === 1
  const isDeclined = comment.commentType === 2

  return (
    <div
      className={`p-3 rounded-lg border text-sm ${
        isCompleted
          ? "bg-blue-50 border-blue-200"
          : isDeclined
            ? "bg-red-50 border-red-200"
            : "bg-muted border-border"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {isCompleted && (
          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
        )}
        {isDeclined && (
          <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
        )}
        {!isCompleted && !isDeclined && (
          <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
        <span
          className={`font-medium ${
            isCompleted
              ? "text-blue-700"
              : isDeclined
                ? "text-red-700"
                : "text-muted-foreground"
          }`}
        >
          {comment.authorEmail}
        </span>
        <span className="text-muted-foreground ml-auto">
          {new Date(comment.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>
      <p
        className={`leading-relaxed ${
          isCompleted
            ? "text-blue-900"
            : isDeclined
              ? "text-red-800"
              : "text-foreground"
        }`}
      >
        {comment.text}
      </p>
    </div>
  )
}

export default function TaskCompletionPage() {
  const { token: urlToken } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token") || urlToken
  const [notes, setNotes] = useState("")
  const [newComment, setNewComment] = useState("")
  const [action, setAction] = useState<"complete" | "decline" | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    data: taskData,
    isLoading,
    error,
  } = useTaskCompletion(token || undefined)
  const completeTask = useCompleteTask(token!)
  const declineTask = useDeclineTask(token!)
  const addComment = useAddComment(token!)
  const { getToken: getCaptchaToken } = useRecaptcha()

  const handleComplete = useCallback(async () => {
    if (!token) return
    setIsSubmitting(true)
    try {
      const captchaToken = await getCaptchaToken("workflow_task_complete")
      await completeTask.mutateAsync({ request: { notes }, captchaToken })
      toast.success("Task completed successfully!")
      setAction(null)
      setNotes("")
    } catch (error: unknown) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to complete task. Please try again.",
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [token, completeTask, notes, getCaptchaToken])

  const handleDecline = useCallback(async () => {
    if (!token || !notes.trim()) {
      toast.error("Please provide a reason for declining this task.")
      return
    }
    setIsSubmitting(true)
    try {
      const captchaToken = await getCaptchaToken("workflow_task_complete")
      await declineTask.mutateAsync({ request: { notes }, captchaToken })
      toast.success("Task declined successfully!")
      setAction(null)
      setNotes("")
    } catch (error: unknown) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to decline task. Please try again.",
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [token, declineTask, notes, getCaptchaToken])

  const handleAddComment = useCallback(async () => {
    if (!token || !newComment.trim()) return
    setIsSubmitting(true)
    try {
      const captchaToken = await getCaptchaToken("workflow_task_comment")
      await addComment.mutateAsync({ request: { text: newComment }, captchaToken })
      toast.success("Comment added.")
      setNewComment("")
    } catch (error: unknown) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to add comment. Please try again.",
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [token, addComment, newComment, getCaptchaToken])

  if (!token) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Invalid URL</h2>
          <p className="text-muted-foreground">The task link is missing or invalid.</p>
        </div>
      </section>
    )
  }

  if (isLoading) {
    return (
      <section className="min-h-screen py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#1b237a] mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Loading task details...</p>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="min-h-screen py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Unable to load task
          </h2>
          <p className="text-muted-foreground mb-6">
            The task link may be expired or invalid. Please contact support.
          </p>
        </div>
      </section>
    )
  }

  const task = taskData!
  const isCompleted = task.status === "Completed"
  const isDeclined = task.status === "Declined"
  const isExpired = task.status === "Expired"
  const isActionable =
    !isCompleted && !isDeclined && !isExpired && task.canComplete
  const hasComments = task.comments.length > 0

  return (
    <section className="min-h-screen py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#1b237a] to-[#1a2a8f] bg-clip-text text-transparent mb-4">
              Task Completion
            </h1>
            <p className="text-lg text-muted-foreground">
              Review and complete your assigned task
            </p>
          </div>

          {/* Task Card */}
          <Card className="mb-8 shadow-lg">
            <CardHeader>
              <div className="flex items-start justify-between">
                <CardTitle className="text-2xl font-semibold text-foreground">
                  {task.title}
                </CardTitle>
                <Badge
                  variant={
                    task.status === "Completed"
                      ? "default"
                      : task.status === "Declined"
                        ? "destructive"
                        : task.status === "Expired"
                          ? "secondary"
                          : "outline"
                  }
                  className="ml-4"
                >
                  {task.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Description */}
              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  Description
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {task.description}
                </p>
              </div>

              {/* Task Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">Assigned to</p>
                    <p className="font-medium text-foreground">
                      {task.assignedToEmail}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">Organisation</p>
                    <p className="font-medium text-foreground">
                      {task.organizationName}
                    </p>
                    <p className="text-sm text-muted-foreground">{task.workflowName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">Due Date</p>
                    <p className="font-medium text-foreground">
                      {new Date(task.dueDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="font-medium text-foreground">{task.status}</p>
                  </div>
                </div>
              </div>

              {/* Comments */}
              {hasComments && (
                <div>
                  <h3 className="font-semibold text-foreground mb-3">Comments</h3>
                  <div className="space-y-2">
                    {task.comments.map((comment) => (
                      <CommentBubble key={comment.id} comment={comment} />
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add Comment (pending tasks only) */}
          {isActionable && (
            <Card className="mb-8 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Add a Comment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Ask a question or leave a note before completing this task..."
                  className="min-h-[80px]"
                />
                <Button
                  onClick={handleAddComment}
                  disabled={isSubmitting || !newComment.trim()}
                  variant="outline"
                  size="sm"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Comment
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Action Section */}
          {isActionable && (
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-foreground">
                  Take Action
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {action === null ? (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      onClick={() => setAction("complete")}
                      className="flex-1"
                      size="lg"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Complete Task
                    </Button>
                    {task.canDecline && (
                      <Button
                        onClick={() => setAction("decline")}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        size="lg"
                      >
                        <XCircle className="w-5 h-5 mr-2" />
                        Decline Task
                      </Button>
                    )}
                  </div>
                ) : action === "complete" ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Completion Notes (Optional)
                      </label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add any notes about the completion of this task..."
                        className="min-h-[100px]"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleComplete}
                        disabled={isSubmitting}
                        className="flex-1"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Confirm Completion
                      </Button>
                      <Button
                        onClick={() => {
                          setAction(null)
                          setNotes("")
                        }}
                        variant="outline"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Reason for Declining{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Please explain why you are declining this task..."
                        className="min-h-[100px]"
                        required
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleDecline}
                        disabled={isSubmitting || !notes.trim()}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4 mr-2" />
                        )}
                        Confirm Decline
                      </Button>
                      <Button
                        onClick={() => {
                          setAction(null)
                          setNotes("")
                        }}
                        variant="outline"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Status Messages */}
          {isCompleted && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Task Completed
              </h2>
              <p className="text-muted-foreground">
                This task has been successfully completed.
              </p>
            </div>
          )}

          {isDeclined && (
            <div className="text-center py-8">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Task Declined
              </h2>
              <p className="text-muted-foreground">This task has been declined.</p>
            </div>
          )}

          {isExpired && (
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Task Expired
              </h2>
              <p className="text-muted-foreground">
                This task has expired and is no longer actionable.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}